import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Award, CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

export default function Festivals() {
  const [festivals, setFestivals] = useState([]);

  useEffect(() => {
    api.get("/festivals").then((r) => setFestivals(r.data));
  }, []);

  const blocks = useMemo(() => {
    if (!festivals.length) return null;
    return Object.fromEntries(festivals.map((f) => [f.id, f.overview]));
  }, [festivals]);
  const { lang, tr, busy } = usePageTranslation(blocks, "festivals");

  const speakText = festivals.map((f) => `${f.name}. ${tr(f.id, f.overview)}`).join(" ");

  return (
    <main data-testid="festivals-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">The Global Festival Calendar</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2 text-[#1c1512]">Festivals & Awards</h1>
      <p className="text-[#6b5d51] mt-3 max-w-2xl">From the Big Three to the great regional gatherings — the events where world cinema is discovered, honored and traded.</p>
      <div className="mt-6">
        <PageToolbar speakText={speakText} busy={busy} lang={lang} />
      </div>

      <div className="mt-10 space-y-5">
        {festivals.map((f, i) => (
          <div key={f.id} data-testid={`festival-card-${f.id}`} className={`grid md:grid-cols-[180px_1fr_220px] gap-5 border border-[#d8ccb8] p-6 ${i % 2 ? "bg-[#efe7d7]" : "bg-[#fbf8f1]"}`}>
            <div>
              <p className="font-display font-bold text-4xl text-[#7d1a1a]">{f.founded}</p>
              <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#8a7c6d] mt-1">Founded</p>
              <span className={`inline-block mt-3 font-mono-label text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${f.tier === "Big Three" ? "bg-[#7d1a1a] text-[#f6f1e7]" : "bg-[#1c1512] text-[#f6f1e7]"}`}>
                {f.tier}
              </span>
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl">{f.name}</h2>
              <div className="flex flex-wrap gap-4 text-sm text-[#6b5d51] mt-2">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#7d1a1a]" /> {f.city}, {f.country}</span>
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#7d1a1a]" /> {f.month}</span>
              </div>
              <p className="text-[#4a3f35] mt-3 leading-relaxed">{tr(f.id, f.overview)}</p>
            </div>
            <div className="md:border-l md:border-[#d8ccb8] md:pl-6">
              <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#8a7c6d]">Top Award</p>
              <p className="flex items-center gap-2 font-display font-semibold text-lg mt-1"><Award className="w-5 h-5 text-[#b06810]" /> {f.top_award}</p>
              {f.country_code && (
                <Link to={`/country/${f.country_code}`} className="inline-block mt-4 text-sm font-medium link-underline text-[#7d1a1a]">
                  {f.country} cinema →
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="font-mono-label text-[10px] tracking-wide text-[#8a7c6d] mt-6">
        Award results follow each festival's official records. Community honors are always labeled separately from official awards.
      </p>
    </main>
  );
}
